import React, { useState, useEffect } from "react";
import { View, StyleSheet, ActivityIndicator } from "react-native";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import ActivityCardList from "../../components/customCoursework/ActivityCardList/ActivityCardList";
import { EditTaskGrey, TrashCanBlue, SparkleBlue } from "../../components/svgs/common";
import { PlusCircle } from "../../components/svgIcons";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import Notifications from "../../components/settings/Notifications/Notifications";
import MoveActivityModal from "../../components/customCoursework/MoveActivityModal/MoveActivityModal";
import GenerateLessonPlanModal from "../../components/customCoursework/GenerateLessonPlanModal/GenerateLessonPlanModal";
import { useAppConfig } from "../../AppConfigProvider";
import { Trash } from "@phosphor-icons/react/dist/csr/Trash";
import { ArrowClockwise } from "@phosphor-icons/react/dist/csr/ArrowClockwise";
import { DownloadSimple } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import Tooltip from "../../components/common/tooltip/Tooltip";

const ModuleContainer = ({
  module,
  allModules,
  onEditModule,
  onDeleteModule,
  onOpenAddActivity,
  onItemOptionsPress,
  onDragEnd,
  errorUpdatingAssessment,
  isUpdatingAssessment,
  onToggleAssessment,
  onMoveActivity,
  onGenerateLessonPlan,
  onDownloadLessonPlan,
  lessonPlanStatus,
  lessonPlanError,
  style = {},
}) => {
  const [isMoveModalVisible, setIsMoveModalVisible] = useState(false);
  const [isLessonPlanModalVisible, setIsLessonPlanModalVisible] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [localModuleData, setLocalModuleData] = useState(module);
  const [isGeneratingLessonPlan, setIsGeneratingLessonPlan] = useState(false);
  const [localLessonPlanStatus, setLocalLessonPlanStatus] = useState({
    isGenerating: false,
    hasLessonPlan: false,
  });
  const { primaryColor } = useAppConfig();

  useEffect(() => {
    setLocalModuleData(module);
  }, [module]);

  useEffect(() => {
    if (lessonPlanStatus) {
      setLocalLessonPlanStatus(lessonPlanStatus);
    }
  }, [lessonPlanStatus]);

  const hasActivities =
    localModuleData?.aiTutors?.length > 0 ||
    localModuleData?.pages?.length > 0 ||
    localModuleData?.axioResources?.length > 0 ||
    localModuleData?.assignments?.length > 0 ||
    localModuleData?.reviews?.length > 0;

  const handleActivityOptionsPress = (action, activity) => {
    if (action === "move") {
      setSelectedActivity(activity);
      setIsMoveModalVisible(true);
    } else {
      // Pass other actions to the parent handler
      onItemOptionsPress(action, activity);
    }
  };

  const handleMoveActivity = async (activityId, sourceModuleId, targetModuleId, activityType) => {
    try {
      if (onMoveActivity) {
        await onMoveActivity(activityId, sourceModuleId, targetModuleId, activityType);
      }

      if (activityType === "Interactive Learning Experience") {
        setLocalModuleData((prev) => ({
          ...prev,
          aiTutors: prev.aiTutors?.filter((item) => item.id !== activityId) || [],
          resourceOrder: prev.resourceOrder?.filter((id) => id !== activityId) || [],
        }));
      } else if (activityType === "Resource") {
        setLocalModuleData((prev) => ({
          ...prev,
          axioResources: prev.axioResources?.filter((item) => item.id !== activityId) || [],
          resourceOrder: prev.resourceOrder?.filter((id) => id !== activityId) || [],
        }));
      } else if (activityType === "Assignment") {
        setLocalModuleData((prev) => ({
          ...prev,
          assignments: prev.assignments?.filter((item) => item.id !== activityId) || [],
          resourceOrder: prev.resourceOrder?.filter((id) => id !== activityId) || [],
        }));
      } else if (activityType === "Review") {
        setLocalModuleData((prev) => ({
          ...prev,
          reviews: prev.reviews?.filter((item) => item.id !== activityId) || [],
          resourceOrder: prev.resourceOrder?.filter((id) => id !== activityId) || [],
        }));
      }

      setIsMoveModalVisible(false);
      setSelectedActivity(null);
    } catch (error) {
      console.error("Error during activity move:", error);
    }
  };

  const handleCloseMoveModal = () => {
    setIsMoveModalVisible(false);
    setSelectedActivity(null);
  };

  const handleOpenLessonPlanModal = () => {
    setIsLessonPlanModalVisible(true);
  };

  const handleCloseLessonPlanModal = () => {
    if (!isGeneratingLessonPlan) {
      setIsLessonPlanModalVisible(false);
    }
  };

  const handleGenerateLessonPlan = async (selectedSections) => {
    try {
      setIsGeneratingLessonPlan(true);

      if (onGenerateLessonPlan) {
        await onGenerateLessonPlan(localModuleData.id, selectedSections);
      }

      setIsLessonPlanModalVisible(false);
    } catch (error) {
      console.error("Error generating lesson plan:", error);
    } finally {
      setIsGeneratingLessonPlan(false);
    }
  };

  const handleDownloadLessonPlan = async () => {
    try {
      if (onDownloadLessonPlan) {
        await onDownloadLessonPlan(localModuleData.id);
      }
    } catch (error) {
      console.error("Error downloading lesson plan:", error);
    }
  };

  // Render lesson plan button based on status and errors
  const renderLessonPlanButton = () => {
    if (localLessonPlanStatus.isGenerating) {
      return (
        <CustomButton
          leftIcon={<ActivityIndicator size="small" color={primaryColor} />}
          text="Generating..."
          size="s"
          styleType="primaryTransparentStroke"
          disabled={true}
          onPress={() => {}}
        />
      );
    }

    if (localLessonPlanStatus.hasLessonPlan) {
      return (
        <View style={{ flexDirection: "row", gap: 8 }}>
          <CustomButton
            leftIcon={<DownloadSimple size={16} weight="bold" color={primaryColor} />}
            text="Download Lesson Plan"
            size="s"
            styleType="primaryTransparentStroke"
            textStyle={{ fontSize: 14, fontWeight: 700 }}
            onPress={handleDownloadLessonPlan}
          />
          <Tooltip content="Regenerate Lesson Plan" horizontalOffset={-90}>
            <CustomButton
              // text="Retry"
              leftIcon={<ArrowClockwise size={16} color={primaryColor} weight="bold" />}
              size="s"
              styleType="primaryTransparentStroke"
              textStyle={{ fontSize: 14, fontWeight: 700 }}
              onPress={handleOpenLessonPlanModal}
            />
          </Tooltip>
        </View>
      );
    }

    return (
      <View style={{ flexDirection: "column", alignItems: "flex-start" }}>
        <CustomButton
          leftIcon={<SparkleBlue width={16} height={16} />}
          text="Generate Lesson Plan"
          size="s"
          styleType="primaryTransparentStroke"
          textStyle={{ fontSize: 14, fontWeight: 700 }}
          onPress={handleOpenLessonPlanModal}
          disabled={isGeneratingLessonPlan}
        />
        {lessonPlanError && (
          <CustomText
            text={lessonPlanError}
            size="xs"
            style={{ color: "#DC2626", marginTop: 4 }}
            useTranslationText={false}
          />
        )}
      </View>
    );
  };

  return (
    <View style={[styles.container, style]}>
      <View style={styles.moduleHeader}>
        <View style={styles.titleContainer}>
          <View style={styles.titleRow}>
            <CustomText text={localModuleData?.title} weight="bold" size="xl" useTranslationText={false} />
            <CustomButton
              leftIcon={<EditTaskGrey width={16} height={16} />}
              size="s"
              onPress={() => onEditModule(localModuleData)}
            />
          </View>
          <CustomSpacing type="vertical" size="m" />
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "flex-start", // Changed from center to flex-start to accommodate error text
              flex: 1,
              width: "100%",
            }}>
            <View style={{ flexDirection: "row", alignItems: "flex-start", flex: 1, gap: 8 }}>
              <CustomButton
                leftIcon={<PlusCircle width={16} height={16} />}
                text="Activity"
                size="s"
                styleType="primaryTransparentStroke"
                textStyle={{ fontSize: 14, fontWeight: 700 }}
                onPress={onOpenAddActivity}
              />
              {renderLessonPlanButton()}
            </View>
            <View style={{ flex: 1, alignItems: "flex-end" }}>
              <CustomButton
                leftIcon={<Trash color="#F63D68" size={16} />}
                text="Delete Module"
                styleType="transparent"
                textStyle={{ fontSize: 14, fontWeight: 700, color: "#F63D68" }}
                size="s"
                onPress={() => onDeleteModule(localModuleData)}
              />
            </View>
          </View>
        </View>
      </View>

      <View style={styles.activitiesSection}>
        <View style={styles.activitiesHeader}>
          <CustomText text="Activities" weight="bold" size="m" />
          <CustomSpacing type="vertical" size="s" />
          <Notifications
            errorMessage={errorUpdatingAssessment}
            isEnabled={localModuleData?.assessment?.isEnabled}
            onChange={onToggleAssessment}
            isLoading={isUpdatingAssessment}
            buttonText={`Assessment is ${localModuleData?.assessment?.isEnabled ? "enabled" : "disabled"} for this module`}
          />
        </View>

        {hasActivities ? (
          <View style={{ paddingHorizontal: 20 }}>
            <ActivityCardList
              module={localModuleData}
              onItemOptionsPress={(action, item) => handleActivityOptionsPress(action, item)}
              style={styles.activityList}
              onDragEnd={onDragEnd}
            />
          </View>
        ) : (
          <View style={styles.noActivitiesContainer}>
            <CustomText
              text="This module doesn't have any Activities yet"
              useTranslationText={false}
              size="m"
              style={styles.noActivitiesText}
            />
          </View>
        )}
      </View>

      {selectedActivity && (
        <MoveActivityModal
          visible={isMoveModalVisible}
          onClose={handleCloseMoveModal}
          modules={allModules}
          currentModuleId={localModuleData.id}
          activityId={selectedActivity.id}
          activityType={selectedActivity.type}
          onMoveActivity={handleMoveActivity}
        />
      )}

      <GenerateLessonPlanModal
        visible={isLessonPlanModalVisible}
        onClose={handleCloseLessonPlanModal}
        onGenerate={handleGenerateLessonPlan}
        isGenerating={isGeneratingLessonPlan}
        moduleTitle={localModuleData?.title}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  moduleHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  titleContainer: {
    flexDirection: "column",
    alignItems: "flex-start",
    flex: 1,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  activitiesSection: {
    flex: 1,
  },
  activitiesHeader: {
    flexDirection: "column",
    justifyContent: "flex-start",
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
  activityList: {
    flex: 1,
  },
  noActivitiesContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FCFCFD",
    margin: 20,
    borderRadius: 10,
    padding: 40,
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  noActivitiesText: {
    color: "#344054",
  },
});

export default ModuleContainer;
